import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import de from '@angular/common/locales/de';
import { LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NoPreloading, provideRouter, TitleStrategy, withPreloading } from '@angular/router';
import {
    DELAB_NGX_AUTH_SERVICE_TOKEN,
    DELAB_NGX_DOCUMENTS_SERVICE_TOKEN,
    DELAB_NGX_I18N_SERVICE_TOKEN,
    DELAB_NGX_ORDER_PROFILES_SELECTION_STEP_SERVICE_TOKEN,
    DELAB_NGX_PATIENTS_SERVICE_TOKEN,
    DELAB_NGX_RECORD_DOCUMENTS_SERVICE_TOKEN,
    DELAB_NGX_RECORDS_SERVICE_TOKEN,
    DELAB_NGX_SHOP_NAVIGATION_SERVICE_TOKEN,
    DELAB_NGX_SHOP_SERVICE_TOKEN,
    DELAB_NGX_SINGLE_REPORT_SERVICE_TOKEN,
    DELAB_NGX_STRUCT_MIBI_SERVICE_TOKEN,
    DelabNgxHeaderInterceptor,
    DelabNgxParseErrorInterceptor,
    DelabNgxUnauthorizedInterceptor,
} from '@delab/ngx-ixconnect-components';
import { HeaderDto } from '@shared/dto/auth/Header.dto';
import { AppComponent } from './app/app.component';
import { CustomPaginatorIntl } from './app/providers/custom-paginator-intl.provider';
import { CustomDateAdapter, customDateFormats } from './app/providers/date-formats';
import { routes } from './app/routes';
import { DocumentsService } from './app/routes/documents-view/services/documents.service';
import { OrderProfileService } from './app/routes/order-creation/services/order-profile.service';
import { PatientsService } from './app/routes/patients-search/services/patients.service';
import { ShopNavigationService } from './app/routes/shop/services/shop-navigation.service';
import { ShopService } from './app/routes/shop/services/shop.service';
import { AuthService } from './app/services/auth.service';
import { IxlabTitleStrategy } from './app/services/custom-title-strategy.service';
import { I18nService } from './app/services/i18n.service';
import { NavigationEventTrackingService } from './app/services/navigation-event-tracking.service';
import { RecordsService } from './app/services/records.service';
import { ReportService } from './app/services/report.service';
import { eagerProvider } from './app/shared/providers';

registerLocaleData(de);

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        eagerProvider(NavigationEventTrackingService),

        { provide: DELAB_NGX_RECORDS_SERVICE_TOKEN, useClass: RecordsService },
        { provide: DELAB_NGX_RECORD_DOCUMENTS_SERVICE_TOKEN, useClass: RecordsService },
        { provide: DELAB_NGX_STRUCT_MIBI_SERVICE_TOKEN, useClass: RecordsService },
        { provide: DELAB_NGX_SINGLE_REPORT_SERVICE_TOKEN, useClass: ReportService },
        { provide: DELAB_NGX_I18N_SERVICE_TOKEN, useClass: I18nService },
        { provide: DELAB_NGX_SHOP_SERVICE_TOKEN, useClass: ShopService },
        { provide: DELAB_NGX_SHOP_NAVIGATION_SERVICE_TOKEN, useClass: ShopNavigationService },
        { provide: DELAB_NGX_DOCUMENTS_SERVICE_TOKEN, useClass: DocumentsService },
        { provide: DELAB_NGX_PATIENTS_SERVICE_TOKEN, useClass: PatientsService },
        { provide: DELAB_NGX_ORDER_PROFILES_SELECTION_STEP_SERVICE_TOKEN, useClass: OrderProfileService },
        { provide: DELAB_NGX_AUTH_SERVICE_TOKEN, useExisting: AuthService },

        { provide: HTTP_INTERCEPTORS, useClass: DelabNgxHeaderInterceptor<HeaderDto>, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: DelabNgxParseErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: DelabNgxUnauthorizedInterceptor<HeaderDto>, multi: true },
        provideHttpClient(withInterceptorsFromDi()),

        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },

        { provide: LOCALE_ID, useValue: 'de-DE' },
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: customDateFormats },

        provideAnimationsAsync(),

        provideRouter(routes, withPreloading(NoPreloading)),
        { provide: TitleStrategy, useClass: IxlabTitleStrategy },
    ],
}).catch((error) => {
    console.error('Error bootstrapping application:', error);
    showErrorPage();
});

function showErrorPage() {
    const body = document.body;

    body.style.backgroundColor = '#fafafa';
    body.classList.add('w-full', 'h-full', 'flex', 'justify-center', 'items-center');

    const card = document.createElement('div');
    card.classList.add('m-2', 'bg-white', 'rounded', 'shadow', 'p-4');

    body.appendChild(card);

    const title = document.createElement('h2');
    title.innerText = 'Fehler beim Starten der Applikation';
    card.appendChild(title);

    const explanation = document.createElement('p');
    explanation.innerText =
        'Beim Starten der Applikation ist ein Fehler aufgetreten. Sollte der Fehler auch nach Neuladen der Seite bestehen bleiben, kontaktieren Sie bitte Ihren Administrator.';
    card.appendChild(explanation);
}
